import { useCallback } from "react"
import type { CombinedError } from "urql"
import { useMutation } from "urql"

import type {
  GoodsReceipt,
  GoodsReceiptMutationProcessReceiptArgs,
  GoodsReceiptUpdateFailure,
  ProcessGoodsReceiptMutation,
} from "@/graphql/codegen/graphql"
import { GoodsReceiptStatus, ProcessGoodsReceiptDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseMarkAsNotReceivedReturn {
  markAsNotReceived: (goodsReceipt: GoodsReceipt) => Promise<GoodsReceipt | null>
  isMarkingAsNotReceived: boolean
  error?: CombinedError
}

export const useMarkAsNotReceived = (onSuccess?: () => void): UseMarkAsNotReceivedReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching: isMarkingAsNotReceived, error }, executeMarkAsNotReceived] = useMutation<
    ProcessGoodsReceiptMutation,
    GoodsReceiptMutationProcessReceiptArgs
  >(ProcessGoodsReceiptDocument)

  const markAsNotReceived = useCallback(
    async (goodsReceipt: GoodsReceipt) => {
      const { data, error } = await executeMarkAsNotReceived({
        input: {
          goodsReceiptId: goodsReceipt.goodsReceiptId,
          status: GoodsReceiptStatus.Incoming,
        },
      })

      const output = data?.goodsReceipt.processReceipt
      const hasError = error || output?.__typename === "GoodsReceiptUpdateFailure"

      if (hasError) {
        const errorMessage =
          error?.message ??
          (output as GoodsReceiptUpdateFailure).error ??
          "Failed to mark goods receipt as not received."
        notify({
          type: NotificationType.Error,
          message: errorMessage,
          silent: false,
        })
        throw new Error(errorMessage)
      }

      notify({
        type: NotificationType.Success,
        message: `Goods Receipt #${goodsReceipt.agrNumber} has been marked as not received`,
        silent: false,
      })

      onSuccess?.()

      return output?.goodsReceipt as GoodsReceipt | null
    },
    [executeMarkAsNotReceived, notify, onSuccess]
  )

  return {
    markAsNotReceived,
    isMarkingAsNotReceived,
    error,
  }
}
